import { useCallback, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getMergedCachedData, setCachedDocument } from "../utils/documentCache";
import { setUiLocked } from "../app/EditorSlice";

/**
 * useDocumentCache
 *
 * Thin orchestration layer over the documentCache module that keeps the cache
 * lifecycle out of the rendering components.
 *
 * @param {string} id        - document id
 * @param {boolean} enabled  - caching applies ONLY to normal documents
 *                             (never templates / read-only / conflict-preview /
 *                             offline / external flows)
 */
export default function useDocumentCache(id, enabled = false) {
  const dispatch = useDispatch();

  // Read the cache exactly ONCE on mount (lazy initializer). A renderable entry
  // here means we can paint instantly without waiting for either API.
  const [cached] = useState(() =>
    enabled && id ? getMergedCachedData(id) : null
  );

  // While the cached preview is on screen we lock edits so the user can't mutate
  // stale content before the fresh fetch swaps in. Kept in redux so the (deeply
  // nested) editor can read it without prop drilling.
  const isUiLocked = useSelector((state) => state.editor.isUiLocked);

  // Guards the single instant paint so we never re-seed the editor with cache.
  const cacheRenderedRef = useRef(false);

  const lockUi = useCallback(() => dispatch(setUiLocked(true)), [dispatch]);
  const unlockUi = useCallback(() => dispatch(setUiLocked(false)), [dispatch]);

  const commitCache = useCallback(
    (documentData, documentInfo) => {
      if (!enabled || !id) return;
      setCachedDocument(id, { documentData, documentInfo });
    },
    [enabled, id]
  );

  return {
    cached,
    isUiLocked,
    lockUi,
    unlockUi,
    cacheRenderedRef,
    commitCache,
  };
}
